import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useAppConfig } from './AppConfigContext';
import AdminUserManager, { AdminUserInfo, AdminNavItem } from '../business/auth/AdminUserManager';

interface AdminContextType {
  adminUserInfo: AdminUserInfo | null;
  navItems: AdminNavItem[];
  isLoading: boolean;
  isHostAdmin: boolean;
  isTenantAdmin: boolean;
  isPartner: boolean;
  isPartnerOwner: boolean;
  isAnyAdmin: boolean;
  hasPermission: (permission: keyof AdminUserInfo['permissions']) => boolean;
  refreshAdminInfo: () => Promise<void>;
}

export const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

interface AdminProviderProps {
  children: ReactNode;
}

export function AdminProvider({ children }: AdminProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const { config } = useAppConfig();
  const [adminUserInfo, setAdminUserInfo] = useState<AdminUserInfo | null>(null);
  const [navItems, setNavItems] = useState<AdminNavItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Tracks whether the FIRST load has completed. Subsequent re-runs of this effect
  // (e.g. when AppConfigContext's `config` reference changes after the initial
  // app-configuration fetch resolves) refresh admin info in the background
  // WITHOUT flipping isLoading=true. Without this, every admin page that gates on
  // `adminLoading` gets unmounted and remounted on each config refresh — and any
  // useEffect on those pages fires its fetch twice on a hard reload.
  const hasLoadedRef = useRef(false);

  const loadAdminInfo = async () => {
    if (!isAuthenticated || !user) {
      setAdminUserInfo(null);
      setNavItems([]);
      setIsLoading(false);
      hasLoadedRef.current = false;
      return;
    }

    if (!hasLoadedRef.current) {
      setIsLoading(true);
    }
    try {
      const [adminInfo, adminNavItems] = await Promise.all([
        AdminUserManager.getAdminUserInfo(config),
        AdminUserManager.getAdminNavItems(config)
      ]);

      setAdminUserInfo(adminInfo);
      setNavItems(adminNavItems);
      hasLoadedRef.current = true;
    } catch {
      setAdminUserInfo(null);
      setNavItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAdminInfo();
  }, [isAuthenticated, user, config]);

  const refreshAdminInfo = async () => {
    await loadAdminInfo();
  };

  const hasPermission = (permission: keyof AdminUserInfo['permissions']) => {
    return adminUserInfo?.permissions[permission] ?? false;
  };

  const value: AdminContextType = {
    adminUserInfo,
    navItems,
    isLoading,
    isHostAdmin: adminUserInfo?.isHostAdmin ?? false,
    isTenantAdmin: adminUserInfo?.isTenantAdmin ?? false,
    isPartner: adminUserInfo?.isPartner ?? false,
    isPartnerOwner: adminUserInfo?.isPartnerOwner ?? false,
    isAnyAdmin: (adminUserInfo?.isHostAdmin || adminUserInfo?.isTenantAdmin || adminUserInfo?.isPartner) ?? false,
    hasPermission,
    refreshAdminInfo
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}